import React, {
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  Navigate,
} from 'react-router-dom';
import {
  useAuth0,
} from '@auth0/auth0-react';
import FullScreenSpinner from 'core/components/FullScreenSpinner';
import {
  APP_ROUTES,
} from 'views/EMR/router';

const SUPPORT_SILENT_AUTH_KEY = 'support_silent_auth_attempted_v1';

type Props = {
  children: ReactNode;
};

/**
 * Auth guard for /support route that:
 * - Tries silent auth to check for existing Auth0 session
 * - Redirects to /support/portal if authenticated
 * - Renders children (login page) if not authenticated
 */
export default function SupportAuthGuard({ children }: Props) {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    loginWithRedirect,
  } = useAuth0();
  const [silentAuthDone, setSilentAuthDone] = useState(
    () => sessionStorage.getItem(SUPPORT_SILENT_AUTH_KEY) === '1',
  );

  useEffect(() => {
    if (isAuthLoading) return;

    // Already authenticated - clear the flag and let render handle redirect
    if (isAuthenticated) {
      sessionStorage.removeItem(SUPPORT_SILENT_AUTH_KEY);
      return;
    }

    // Not authenticated - try silent auth once to check for existing Auth0 session
    if (!silentAuthDone) {
      sessionStorage.setItem(SUPPORT_SILENT_AUTH_KEY, '1');
      loginWithRedirect({
        authorizationParams: {
          prompt: 'none',
        },
        appState: { targetUrl: APP_ROUTES.POPULATE_SUPPORT.PORTAL },
      });
    }
  }, [isAuthLoading, isAuthenticated, silentAuthDone, loginWithRedirect]);

  // Handle Auth0 login_required error callback - mark silent auth as done
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'login_required') {
      setSilentAuthDone(true);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  if (isAuthLoading) {
    return <FullScreenSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to={APP_ROUTES.POPULATE_SUPPORT.PORTAL} replace />;
  }

  return silentAuthDone
    ? children
    : <FullScreenSpinner />;
}
